import { useMemo, useCallback } from "react"
import { chain } from "lodash"
import { usePositionSetMetadata } from "resources/position-sets"
import { combineStatuses, combineTanStackStatuses, DataStatus, OK, ResourceResult } from "helpers/data-statuses"
import { computeModeFilteredSegment } from "helpers/segments"
import { useIsIntradayMode } from "connected-components/dashboard/hooks/use-intraday-state"
import { VIEW_TYPES } from "helpers/base"
import { useTagCategories } from "resources/tags"
import { useAttributeCategories } from "resources/attributes"
import { useClassifications } from "resources/classifications"
import { getAllAvailableRollups } from "helpers/rollups"
import { IsoDate } from "helpers/types/dates"
import { SelectedInstrument } from "helpers/types/instruments/selected"
import { InstrumentMetadata, InstrumentWithMetadata } from "helpers/types/instruments/metadata"
import { CoverageResourceType, normalizeCoverageResourceType } from "helpers/models"
import { INSTRUMENT_TYPES, SELECTED_MODE_VALUES } from "./mode-constants"
import type { InstrumentTypes, SelectedMode } from "./mode-constants"

// [TODO:TS] Put somewhere else
interface ModelMetadata {
  availability: {
    securitiesStartDate: IsoDate
    currentDate: IsoDate
  }
}

// [TODO:TS] Put somewhere else
export type Normalization = "STANDALONE" | "CONTRIBUTION"

// [TODO:TS] Implement type
type Filter = object

const COMPARISON_INSTRUMENT = {
  metricsSuffix: "-comparison",
  suffix: "Comparison",
}

export const INSTRUMENTS = {
  rebalanced: {
    metricsSuffix: "-rebalanced",
    suffix: "Rebalanced",
  },
  original: {
    metricsSuffix: "",
    suffix: "",
  },
  comparison: COMPARISON_INSTRUMENT,
  optimized: COMPARISON_INSTRUMENT,
  end: {
    metricsSuffix: "",
    suffix: "",
  },
  start: COMPARISON_INSTRUMENT,
  target: {
    metricsSuffix: "-target",
    suffix: "Target",
  },
  delta: {
    metricsSuffix: "-delta",
    suffix: "Delta",
    hasDataGetter: true,
  },
} as const

export {
  INSTRUMENT_TYPES,
  SELECTED_MODE_VALUES,
  REBALANCED_MODES,
  COMPARISON_MODES,
  END_VS_START_MODES,
} from "./mode-constants"
export type { InstrumentTypes, SelectedMode } from "./mode-constants"

export const INSTRUMENTS_BY_MODE: Record<SelectedMode, InstrumentTypes[]> = {
  [SELECTED_MODE_VALUES.original]: [INSTRUMENT_TYPES.original],
  [SELECTED_MODE_VALUES.rebalanced]: [INSTRUMENT_TYPES.rebalanced],
  [SELECTED_MODE_VALUES.comparison]: [INSTRUMENT_TYPES.comparison],
  [SELECTED_MODE_VALUES.start]: [INSTRUMENT_TYPES.comparison],
  [SELECTED_MODE_VALUES.end]: [INSTRUMENT_TYPES.original],
  [SELECTED_MODE_VALUES.optimized]: [INSTRUMENT_TYPES.comparison],
  [SELECTED_MODE_VALUES.rebalancedVsOriginal]: [INSTRUMENT_TYPES.rebalanced, INSTRUMENT_TYPES.original, INSTRUMENT_TYPES.delta],
  [SELECTED_MODE_VALUES.rebalancedVsComparison]: [INSTRUMENT_TYPES.rebalanced, INSTRUMENT_TYPES.comparison, INSTRUMENT_TYPES.delta],
  [SELECTED_MODE_VALUES.originalVsComparison]: [INSTRUMENT_TYPES.original, INSTRUMENT_TYPES.comparison, INSTRUMENT_TYPES.delta],
  [SELECTED_MODE_VALUES.optimizedVsOriginal]: [INSTRUMENT_TYPES.comparison, INSTRUMENT_TYPES.original, INSTRUMENT_TYPES.delta],
  [SELECTED_MODE_VALUES.optimizedVsRebalanced]: [INSTRUMENT_TYPES.comparison, INSTRUMENT_TYPES.rebalanced, INSTRUMENT_TYPES.delta],
  [SELECTED_MODE_VALUES.endVsStart]: [INSTRUMENT_TYPES.original, INSTRUMENT_TYPES.comparison, INSTRUMENT_TYPES.delta],
  [SELECTED_MODE_VALUES.rebalancedVsEnd]: [INSTRUMENT_TYPES.rebalanced, INSTRUMENT_TYPES.original, INSTRUMENT_TYPES.delta],
  [SELECTED_MODE_VALUES.rebalancedVsStart]: [INSTRUMENT_TYPES.rebalanced, INSTRUMENT_TYPES.comparison, INSTRUMENT_TYPES.delta],
}

interface ModeSelectorOption {
  id: SelectedMode
  primaryText: string
  group: number
  needComparison?: boolean
  needRebalanced?: boolean
}

export const MODE_SELECTOR_OPTIONS: Record<SelectedMode, ModeSelectorOption> = {
  [SELECTED_MODE_VALUES.rebalancedVsOriginal]: { id: SELECTED_MODE_VALUES.rebalancedVsOriginal, primaryText: "Rebalanced [R] vs Original [O]", needRebalanced: true, group: 0 },
  [SELECTED_MODE_VALUES.rebalancedVsComparison]: { id: SELECTED_MODE_VALUES.rebalancedVsComparison, primaryText: "Rebalanced [R] vs Comparison [C]", needRebalanced: true, needComparison: true, group: 0 },
  [SELECTED_MODE_VALUES.originalVsComparison]: { id: SELECTED_MODE_VALUES.originalVsComparison, primaryText: "Original [O] vs Comparison [C]", needComparison: true, group: 0 },
  [SELECTED_MODE_VALUES.optimizedVsOriginal]: { id: SELECTED_MODE_VALUES.optimizedVsOriginal, primaryText: "Target [T] vs Original [O]", needComparison: true, group: 0 },
  [SELECTED_MODE_VALUES.optimizedVsRebalanced]: { id: SELECTED_MODE_VALUES.optimizedVsRebalanced, primaryText: "Target [T] vs Rebalanced [R]", needRebalanced: true, needComparison: true, group: 0 },
  [SELECTED_MODE_VALUES.rebalancedVsEnd]: { id: SELECTED_MODE_VALUES.rebalancedVsEnd, primaryText: "Rebalanced [R] vs End [E]", needRebalanced: true, group: 0 },
  [SELECTED_MODE_VALUES.rebalancedVsStart]: { id: SELECTED_MODE_VALUES.rebalancedVsStart, primaryText: "Rebalanced [R] vs Start [S]", needRebalanced: true, group: 0 },
  [SELECTED_MODE_VALUES.endVsStart]: { id: SELECTED_MODE_VALUES.endVsStart, primaryText: "End [E] vs Start [S]", needComparison: true, group: 0 },
  [SELECTED_MODE_VALUES.rebalanced]: { id: SELECTED_MODE_VALUES.rebalanced, primaryText: "Rebalanced [R]", needRebalanced: true, group: 1 },
  [SELECTED_MODE_VALUES.original]: { id: SELECTED_MODE_VALUES.original, primaryText: "Original [O]", group: 1 },
  [SELECTED_MODE_VALUES.comparison]: { id: SELECTED_MODE_VALUES.comparison, primaryText: "Comparison [C]", needComparison: true, group: 1 },
  [SELECTED_MODE_VALUES.optimized]: { id: SELECTED_MODE_VALUES.optimized, primaryText: "Target [T]", needComparison: true, group: 1 },
  [SELECTED_MODE_VALUES.end]: { id: SELECTED_MODE_VALUES.end, primaryText: "End [E]", group: 1 },
  [SELECTED_MODE_VALUES.start]: { id: SELECTED_MODE_VALUES.start, primaryText: "Start [S]", needComparison: true, group: 1 },
}

export const DEFAULT_MODE_SELECTOR_OPTIONS = [
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.rebalancedVsOriginal],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.rebalancedVsComparison],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.originalVsComparison],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.rebalanced],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.original],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.comparison],
]

export const END_VS_START_MODE_SELECTOR_OPTIONS = [
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.rebalancedVsEnd],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.rebalancedVsStart],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.endVsStart],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.rebalanced],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.end],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.start],
]

export const OPTIMIZATION_SELECTOR_OPTIONS = [
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.rebalancedVsOriginal],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.optimizedVsRebalanced],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.optimizedVsOriginal],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.rebalanced],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.original],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.optimized],
]

export function getInstrumentOrder(mode: SelectedMode): InstrumentTypes[] {
  switch (mode) {
    case SELECTED_MODE_VALUES.rebalancedVsComparison:
      return ["rebalanced", "comparison"]
    case SELECTED_MODE_VALUES.originalVsComparison:
      return ["original", "comparison"]
    case SELECTED_MODE_VALUES.rebalancedVsOriginal:
      return ["rebalanced", "original"]
    case SELECTED_MODE_VALUES.rebalanced:
      return ["rebalanced"]
    case SELECTED_MODE_VALUES.comparison:
      return ["comparison"]
    default:
      return ["original"]
  }
}

// Order of columns:
// | Original   | Rebalanced or Comparison | Delta |
// | Rebalanced | Comparison               | Delta |
export function useModeInstruments(instrument: InstrumentTypes, secondInstrument: InstrumentTypes, targetInstrument: InstrumentTypes, mode: SelectedMode, shouldSkipDelta: boolean): InstrumentTypes[] {
  return useMemo(() => {
    switch (mode) {
      case SELECTED_MODE_VALUES.rebalancedVsComparison:
      case SELECTED_MODE_VALUES.rebalancedVsStart:
        return shouldSkipDelta ? [targetInstrument] : [secondInstrument, targetInstrument]
      case SELECTED_MODE_VALUES.originalVsComparison:
        return [instrument, targetInstrument]
      case SELECTED_MODE_VALUES.endVsStart:
        return shouldSkipDelta ? [instrument] : [instrument, targetInstrument]
      case SELECTED_MODE_VALUES.rebalancedVsOriginal:
      case SELECTED_MODE_VALUES.rebalancedVsEnd:
        return shouldSkipDelta ? [instrument] : [secondInstrument, instrument]
      case SELECTED_MODE_VALUES.optimizedVsOriginal:
        return [targetInstrument, instrument]
      case SELECTED_MODE_VALUES.optimizedVsRebalanced:
        return shouldSkipDelta ? [targetInstrument] : [targetInstrument, secondInstrument]
      case SELECTED_MODE_VALUES.rebalanced:
        return [secondInstrument]
      case SELECTED_MODE_VALUES.comparison:
      case SELECTED_MODE_VALUES.optimized:
      case SELECTED_MODE_VALUES.start:
        return [targetInstrument]
      default:
      case SELECTED_MODE_VALUES.original:
      case SELECTED_MODE_VALUES.end:
        return [instrument]
    }
  }, [instrument, secondInstrument, targetInstrument, mode, shouldSkipDelta])
}

export function useInstrumentsWithMetadata(modelId: string, currentDate: IsoDate, instruments: Record<string, SelectedInstrument>): [Record<string, InstrumentWithMetadata>, DataStatus] {
  // [TODO:TS] Type those!
  const [originalMetadata, originalMetadataStatus] = usePositionSetMetadata(modelId, instruments.original, currentDate) as ResourceResult<InstrumentMetadata>
  const [comparisonMetadata, comparisonMetadataStatus] = usePositionSetMetadata(modelId, instruments.comparison, currentDate) as ResourceResult<InstrumentMetadata>
  const [baseMetadata, baseMetadataStatus] = usePositionSetMetadata(modelId, instruments.base, currentDate) as ResourceResult<InstrumentMetadata>
  const status = combineStatuses([originalMetadataStatus, comparisonMetadataStatus, baseMetadataStatus])

  const instrumentsWithMetadata = useMemo(
    (): Record<string, InstrumentWithMetadata> => ({
      original: { ...instruments.original, ...originalMetadata },
      ...(instruments.comparison && { comparison: { ...instruments.comparison, ...comparisonMetadata } }),
      ...(instruments.rebalanced && { rebalanced: { ...instruments.rebalanced, ...originalMetadata, name: `${originalMetadata?.name} (Rebalanced)` } }),
      ...(instruments.base && { base: { ...instruments.base, ...baseMetadata } }),
      // [TODO:TS] Fix the as
    } as Record<string, InstrumentWithMetadata>),
    [instruments.original, instruments.comparison, instruments.rebalanced, instruments.base, originalMetadata, comparisonMetadata, baseMetadata])

  return [instrumentsWithMetadata, status]
}

export function useInstrumentsEarliestAndLatestDates(modelMetadata: ModelMetadata, instrumentsWithMetadata: Record<string, InstrumentWithMetadata>) {
  return useMemo((): [IsoDate, IsoDate] => {
    const earliestDate = chain(instrumentsWithMetadata)
      .pick(["original", "comparison", "base"])
      .map(instrument => instrument.earliestDate)
      .concat(modelMetadata.availability.securitiesStartDate)
      .max()
      .value()

    const latestDate = chain(instrumentsWithMetadata)
      .pick(["original", "comparison", "base"])
      .map(instrument => instrument.latestDate)
      .concat(modelMetadata.availability.currentDate)
      .min()
      .value()

    return [earliestDate, latestDate]
  }, [instrumentsWithMetadata, modelMetadata])
}

type AugmentedInstrumentWithMedata = InstrumentWithMetadata & { segment: object | null, expand: boolean }

// Mode-aware query segment for the current dashboard filter. Use this at every query path so the
// instrument and base populations stay consistent — see computeModeFilteredSegment.
export function useFilteredSegment(filter?: Filter, normalization?: Normalization): object | null {
  const isIntradayMode = useIsIntradayMode()
  return useMemo(() =>
    computeModeFilteredSegment(filter, normalization, isIntradayMode),
  [filter, normalization, isIntradayMode])
}

export function useInstrumentsWithQueryOptions(instruments: Record<string, InstrumentWithMetadata>, selectedMode: SelectedMode, { filter, normalization, isExpanded }: {
  filter?: Filter
  normalization?: Normalization
  isExpanded: boolean
}) {
  const segment: object | null = useFilteredSegment(filter, normalization)

  const makeAugmentedInstrument = useCallback((instrument: InstrumentWithMetadata): AugmentedInstrumentWithMedata => ({
    ...instrument,
    segment,
    expand: isExpanded,
  }), [segment, isExpanded])

  // If we rebalance an instrument down to 0, we still want the instrument to appear in the table, so we query the original instrument so the position data is not null
  const instrumentTypesToUse = useMemo(() => {
    const instrumentTypes = INSTRUMENTS_BY_MODE[selectedMode] || []
    if (instrumentTypes.includes(INSTRUMENT_TYPES.rebalanced) && !instrumentTypes.includes(INSTRUMENT_TYPES.original)) {
      return [...instrumentTypes, INSTRUMENT_TYPES.original]
    }
    return instrumentTypes
  }, [selectedMode])

  return useMemo(() => {
    return instrumentTypesToUse.reduce((acc, instrumentType) => {
      if (!instruments[instrumentType]) {
        return acc
      }
      acc[instrumentType] = makeAugmentedInstrument(instruments[instrumentType])
      return acc
    }, {} as Record<InstrumentTypes, AugmentedInstrumentWithMedata>)
  }, [instruments, instrumentTypesToUse, makeAugmentedInstrument])
}

// [TODO:TS]
export function useBaseWithSegment(base: InstrumentWithMetadata | null, filter?: Filter, normalization?: Normalization): InstrumentWithMetadata | null {
  const segment = useFilteredSegment(filter, normalization)
  return useMemo(() => {
    if (!base) {
      return null
    }

    if (!segment) {
      return base
    }

    return {
      ...base,
      segment,
    }
  }, [base, segment])
}

export function useAvailableGroupByOptions(instrument: InstrumentWithMetadata, date: IsoDate, { isActive = false, hasMultipleUniqueInstruments = false }): [unknown[], DataStatus] {
  const viewType = isActive ? VIEW_TYPES.ACTIVE : VIEW_TYPES.ABSOLUTE
  const [tagCategories, tagCategoriesStatus] = useTagCategories(instrument)
  const [attributeCategories, attributeCategoriesStatus] = useAttributeCategories(instrument)
  const [classifications, classificationsStatus] = useClassifications()

  const status = combineTanStackStatuses([tagCategoriesStatus, attributeCategoriesStatus, classificationsStatus])

  const accountRollups = useMemo(() => ({
    classifications,
    tagCategories: tagCategories,
    attributeCategories: attributeCategories,
  }), [tagCategories, attributeCategories, classifications])

  const rollups = useMemo(() => {
    if (status !== OK) {
      return []
    }
    // [TODO:TS] Fix when rollups get typed
    return getAllAvailableRollups(accountRollups as never, date, viewType, hasMultipleUniqueInstruments)
  }, [accountRollups, date, viewType, status, hasMultipleUniqueInstruments])

  return [rollups, status]
}

export function getOrderedInstrumentColumns(mode: SelectedMode): InstrumentTypes[] {
  const instrumentColumns = INSTRUMENTS_BY_MODE[mode]
  if (!instrumentColumns) {
    return ["original"]
  }
  return instrumentColumns
}

export interface CoverageResource {
  resourceType: CoverageResourceType
  resourceId: string
}

// Single derivation for the model selector's coverage gate — both the top-bar and the instrument
// search dialog read the dashboard's currently selected instrument through this, so they can't
// diverge on how resourceType/resourceId are computed. Unrecognized/unsupported instrument types
// (e.g. a selected benchmark) resolve to null: no coverage gate is applied.
export function getCoverageResourceFromInstrument(instrument: SelectedInstrument | null | undefined): CoverageResource | null {
  if (!instrument || typeof instrument.type !== "string" || typeof instrument.id !== "string") {
    return null
  }
  const resourceType = normalizeCoverageResourceType(instrument.type)
  if (!resourceType) {
    return null
  }
  return { resourceType, resourceId: instrument.id }
}
